import { IFetchResponse, MailboxItem, SetFlagsParams, ThreadMessage } from "@preveil-api";
import {
  Account, CollectionAPI, GlobalErrorMessages, MailErrorMessages, Message, MessageHandlerDisplayType, DefaultMailboxes, SetEmailFlagApiArg, UpdateFlagsApiArg, GlobalConstants,
  getVirtualMailboxId, MailFlags, MailSuccessMessages, getMailboxCollectionIdByRole, VIRTUAL_MAILBOXES_SERVER_IDS
} from "src/common";
import { uiActions } from "../";
import { MailActionTypes, mailActions } from "./slice";
import _ from "lodash";
import { all, fork, call, put, takeEvery, select } from "redux-saga/effects";

// -----------------------------------------------------------------------------------
// Description: Get Mail threads from Crypto or collection depending on build mode
// -----------------------------------------------------------------------------------
function* fetchMailThreadsRequest(action: ReturnType<typeof mailActions.getMailThreads>): Generator {
  const current_account = (yield select((state) => state.account.current_account)) as Account;
  // NOTE: Use crypto call to refresh mail list after delete, move actions.
  if (!!current_account) {
    const response = (yield call(CollectionAPI.getMailThreads, current_account, action.payload)) as IFetchResponse;
    if (!response.isError) { // Success Block:
      yield put(mailActions.setThreadsSuccess(response.data));
      !!action.payload.success_message &&
        (yield put(uiActions.handleSetMessage(new Message(action.payload.success_message))));
    } else {
      yield put(mailActions.setThreadsError());
      yield put(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_getting_mail_threads, MessageHandlerDisplayType.toastr), response));
    }
  } else {
    yield put(uiActions.handleRequestErrors(new Message(GlobalErrorMessages.no_current_account_set, MessageHandlerDisplayType.logger)));
  }
}

function* watchFetchMailThreadsRequestRequest() {
  yield takeEvery(MailActionTypes.FETCH_MAIL_THREADS, fetchMailThreadsRequest);
}

// ---------------------------------------------------------------------------------------------------------------------
// Description: Get Mail threads from Crypto client mailboxes (unread, starred) or backup call for others
// NOTE: outbox has been removed from Web V2
// ---------------------------------------------------------------------------------------------------------------------
// Description: Call Web unseen or starred
function* fetchClientMailMainRequest(action: ReturnType<typeof mailActions.getClientMailThreads>): Generator {
  const current_account = (yield select((state) => state.account.current_account)) as Account;
  const is_unread = action.payload.mailbox_id === VIRTUAL_MAILBOXES_SERVER_IDS.unread;
  const response = (yield call(CollectionAPI.getVirtualMailboxThreads, current_account, action.payload, is_unread)) as IFetchResponse;
  if (!response.isError) {
    yield put(mailActions.setThreadsSuccess(response.data));
    !!action.payload.success_message &&
      (yield put(uiActions.handleSetMessage(new Message(action.payload.success_message))));
  } else {
    yield put(mailActions.setThreadsError());
    yield put(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_getting_mail_threads, MessageHandlerDisplayType.toastr), response));
  }
}

function* watchFetchClientMailThreadsRequestRequest() {
  yield takeEvery(MailActionTypes.FETCH_CLIENT_MAIL_THREADS, fetchClientMailMainRequest);
}

// ---------------------------------------------------------------------------------------------------------------------
// Description: Refresh current mailbox from state
// ---------------------------------------------------------------------------------------------------------------------
function* fetchCurrentMailThreads(action: ReturnType<typeof mailActions.updateFlags>): Generator {
  const current_account = (yield select((state) => state.account.current_account)) as Account;
  const mailbox_id = (yield select((state) => state.mail.current_mailbox.mailbox_id)) as string;
  if (!!current_account && !!mailbox_id) {
    const virtual_mailbox_id = getVirtualMailboxId(mailbox_id);
    const params = {
      user_id: current_account.user_id,
      collection_id: current_account.mail_cid,
      mailbox_id: !!virtual_mailbox_id ? virtual_mailbox_id : mailbox_id,
      limit: GlobalConstants.LIMIT_NUMBER_THREAD_TO_FETCH,
      offset: action.payload.offset || 0,
      success_message: action.payload.success_message
    };
    yield put(!!virtual_mailbox_id ? mailActions.getClientMailThreads(params) : mailActions.getMailThreads(params));
  } else {
    yield put(uiActions.handleRequestErrors(new Message(GlobalErrorMessages.no_current_account_set, MessageHandlerDisplayType.logger)));
  }
}

function* watchFetchCurrentMailThreadsRequest() {
  yield takeEvery(MailActionTypes.REFRESH_MAIL_THREADS, fetchCurrentMailThreads);
}

// -----------------------------------------------------------------------------------
// Description: Get Mail Thread from Collection
// -----------------------------------------------------------------------------------
// Separate the web and client call in this sub saga
function* fetchCurrentThreadMainRequest(action: ReturnType<typeof mailActions.getCurrentThread>): Generator {
  const current_account = (yield select((state) => state.account.current_account)) as Account;
  if (!!current_account) {
    const response = (yield call(CollectionAPI.getMailThread, current_account, action.payload, action.payload.mailbox_id)) as IFetchResponse;
    if (!response.isError) {
      yield put(mailActions.setCurrentThreadSuccess(response.data));
    } else {
      yield put(mailActions.resetCurrentThread());
      yield put(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_getting_mail_threads, MessageHandlerDisplayType.toastr), response));
    }
  } else {
    yield put(uiActions.handleRequestErrors(new Message(GlobalErrorMessages.no_current_account_set, MessageHandlerDisplayType.logger)));
  }
}

function* watchFetchCurrentThreadMainRequest() {
  yield takeEvery(MailActionTypes.FETCH_CURRENT_THREAD, fetchCurrentThreadMainRequest);
}

// -----------------------------------------------------------------------------------------------------
// Description: Set flags for an array of messages/threads Crypto or collection depending on build mode
// NOTES:       Handles both web and crypto by calling getCollectionFlagsParams or getLocalFlagsParams
//              Handles both Add and remove flags: 
//               in crypto, it is handled in the url of the CryptoAPI.updateFlags call (pass in params)
//               in Collection it is handled by updating the params directly to most current flags state
//                  (i.e{ ... flag: ["//Flagged", //Seen] to flag: ["//Seen"] })
// RESULT:       Updates will happen automatically on server change (notify_ws), so only handling errors
// NOTE:         On success update threads in case notify does not work
// -----------------------------------------------------------------------------------------------------
function* fetchSetFlagsRequest(action: ReturnType<typeof mailActions.updateFlags>): Generator {
  // const is_web = AppConfiguration.buildForWeb();
  const current_account = (yield select((state) => state.account.current_account)) as Account;
  if (!!current_account) {
    const response = (yield call(CollectionAPI.updateFlags, current_account, getCollectionFlagsParams(current_account, action.payload))) as IFetchResponse;
    if (!response.isError) { // Success: 
      yield put(mailActions.refreshCurrentMailThreads({ offset: action.payload.offset, success_message: action.payload.success_message }));
      yield put(mailActions.setRefreshCounts(true));
    } else {
      yield put(mailActions.setNewMail(true));
      yield put(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_setting_mail_flag, MessageHandlerDisplayType.toastr), response));
    }
  } else {
    yield put(uiActions.handleRequestErrors(new Message(GlobalErrorMessages.no_current_account_set, MessageHandlerDisplayType.logger)));
  }
}

function* watchFetchSetFlagsRequest() {
  yield takeEvery(MailActionTypes.SET_FLAGS, fetchSetFlagsRequest);
}

// -----------------------------------------------------------------------------------
// Description: Delete Mail from  Collection
// -----------------------------------------------------------------------------------
function* fetchWebDeleteThreadsRequest(action: ReturnType<typeof mailActions.deleteMail>): Generator {
  const current_account = (yield select((state) => state.account.current_account)) as Account;
  const default_mailboxes = (yield select((state) => state.mail.default_mailboxes)) as MailboxItem[];
  const trash_mailbox_id = !!default_mailboxes ? getMailboxCollectionIdByRole(DefaultMailboxes.deleted, default_mailboxes) : null;
  const draft_mailbox_id = !!default_mailboxes ? getMailboxCollectionIdByRole(DefaultMailboxes.drafts, default_mailboxes) : null;
  const messages = action.payload.messages;
  const offset = action.payload.offset || 0;
  const mailbox_id = action.payload.mailbox_id;
  if (!!current_account && !!trash_mailbox_id) {
    if ([trash_mailbox_id, draft_mailbox_id].includes(mailbox_id)) {
      // NOTE: Filter to only messages belonging to the current mailbox. Threads can include messages
      // from other mailboxes (e.g. the original inbox email appears in a Drafts thread). Without this
      // filter, those foreign messages get hard-deleted by unique_id and are permanently lost.
      const mailbox_messages = messages.filter((m: ThreadMessage) => m.mailbox_id === mailbox_id);
      const deletions = mailbox_messages.map((m: ThreadMessage) => ({
        collectionId: current_account.mail_cid,
        mbid: mailbox_id,
        id: m.unique_id
      }));
      const response = (yield call(CollectionAPI.deleteMailByColIdMbidMessId, current_account, deletions)) as IFetchResponse;
      if (!response.isError) {
        yield all([
          put(mailActions.decrementDraftNotifications(deletions.length)),
          put(mailActions.refreshCurrentMailThreads({ offset: action.payload.offset, success_message: MailSuccessMessages.messages_deleted_success }))
        ]);
      } else {
        yield put(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_deleting_messages, MessageHandlerDisplayType.toastr), response));
      }
    } else {
      const response = (yield call(CollectionAPI.copyMailThreads, current_account, {
        collectionId: current_account.mail_cid,
        mbid: trash_mailbox_id,
        source_mbid: mailbox_id,
        uids: _.map(messages, (message: ThreadMessage) => (message.uid))
      })) as IFetchResponse;
      if (!response.isError) { // On success update flags
        yield put(mailActions.updateFlags({
          messages,
          flag: MailFlags.DELETED_FLAG,
          remove: false,
          offset,
          success_message: MailSuccessMessages.messages_deleted_success
        }));
      } else {
        yield put(mailActions.setDeleteMailError());
        yield put(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_deleting_messages, MessageHandlerDisplayType.toastr), response));
      }
    }
  } else {
    yield put(uiActions.handleRequestErrors(new Message(GlobalErrorMessages.no_current_account_set, MessageHandlerDisplayType.logger)));
  }
}

// NOTE: Separate the web and client call in this sub saga
function* fetchDeleteThreadsMainRequest(action: ReturnType<typeof mailActions.deleteMail>): Generator {
  const params = {
    type: MailActionTypes.DELETE_MAIL_THREADS,
    payload: action.payload
  };
  yield fetchWebDeleteThreadsRequest(params);
}

function* watchFetchDeleteThreadsRequestRequest() {
  yield takeEvery(MailActionTypes.DELETE_MAIL_THREADS, fetchDeleteThreadsMainRequest);
}



// -----------------------------------------------------------------------------------------------------
// Description: Move Mail from source mailbox to another
// -----------------------------------------------------------------------------------------------------
function* fetchWebMoveMailRequest(action: ReturnType<typeof mailActions.moveMail>): Generator {
  const current_account = (yield select((state) => state.account.current_account)) as Account;
  if (!!current_account) {
    const response = (yield call(CollectionAPI.moveMail, current_account, action.payload)) as IFetchResponse;
    if (!response.isError) { // Success:
      yield put(mailActions.refreshCurrentMailThreads({ offset: 0, success_message: MailSuccessMessages.messages_moved_success }));
    } else {
      yield put(uiActions.handleRequestErrors(new Message(MailErrorMessages.error_moving_messages, MessageHandlerDisplayType.toastr), response));
    };
  } else {
    yield put(uiActions.handleRequestErrors(new Message(GlobalErrorMessages.no_current_account_set, MessageHandlerDisplayType.logger)));
  }
}

function* watchFetchMoveMailRequestRequest() {
  yield takeEvery(MailActionTypes.MOVE_MAIL, fetchWebMoveMailRequest);
}


// ----------------------------------------------------------------
// We can also use `fork()` here to split our saga into multiple watchers.
export function* mailSaga() {
  yield all([
    fork(watchFetchMailThreadsRequestRequest),
    fork(watchFetchClientMailThreadsRequestRequest),
    fork(watchFetchCurrentMailThreadsRequest),
    fork(watchFetchCurrentThreadMainRequest),
    fork(watchFetchSetFlagsRequest),
    fork(watchFetchDeleteThreadsRequestRequest),
    fork(watchFetchMoveMailRequestRequest)
  ]);
}

// Exporting Sagas for testing
export { fetchMailThreadsRequest };

// -----------------------------------------------------------------------------------------------------------------------------
// Mail Saga Utilities
// -----------------------------------------------------------------------------------------------------------------------------
// Description: Account Get Correct step depending on server error code - returns emails: unique_ids[]
export function getLocalFlagsParams(params: SetFlagsParams): SetEmailFlagApiArg {
  return {
    emails: _.map(params.messages, (message: ThreadMessage) => (message.unique_id)),
    flag: params.flag
  };
}

// Description: Bulk updates flags associated with a set of messages. See below for notes on this method.
export function getCollectionFlagsParams(current_account: Account, params: SetFlagsParams): UpdateFlagsApiArg[] {
  const _update_flags_arg: UpdateFlagsApiArg[] = _.map(params.messages, (message: ThreadMessage) => {
    const flags = !!params.remove ? _.filter(message.flags, (flag: string) => (params.flag !== flag)) :
      (message.flags.slice()).concat([params.flag]);
    return {
      collectionId: current_account.mail_cid,
      mbid: message.mailbox_id,
      updates: [{
        id: message.unique_id,
        last_version: message.version || "",
        flags: _.uniq(flags) // NOTE: Remove any duplicated flags
      }]
    };
  });
  // Group by collectionId and  mailbox_id and merge updates into array
  const update_flags_arg: UpdateFlagsApiArg[] = [];
  const grouped_args = _.groupBy(_update_flags_arg, item => `"${item.mbid}+${item.collectionId}"`);
  _.forEach(grouped_args, (update_flags: UpdateFlagsApiArg[]) => {
    update_flags_arg.push({
      collectionId: update_flags[0].collectionId,
      mbid: update_flags[0].mbid,
      updates: _.flatten(_.map(update_flags, (flag: UpdateFlagsApiArg) => flag.updates))
    });
  });
  return update_flags_arg;
}
